import React, { useCallback, useEffect, useState } from 'react';
import './style.less';
import { Icon } from 'shared/components/Icon';
import { SalesfloorRoomSettingsItem } from 'browser/components/SalesfloorRoom/SettingsModal/SettingsItem';
import { useAppDispatch, useAppSelector } from 'shared/store';
import detectrtc from 'detectrtc';
import {
  clearPreferredMediaDeviceError,
  setPreferredMediaDevices,
} from 'shared/store/dashboard-slice';
import { Popover, notification } from 'antd';
import { selectOutgoingCameraStream } from 'shared/store/realtime-slice';

export enum SettingItemType {
  Cam = 'cam',
  Mic = 'mic',
  Speaker = 'speaker',
}

export const ChatPopupActiveCallSettings = () => {
  const [hasMediaPermissions, setHasMediaPermissions] = useState(false);
  const camStreamOut = useAppSelector(selectOutgoingCameraStream);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();

  const { isOutgoingAudioEnabled } = useAppSelector(s => s.dashboard);

  const {
    audioDevices,
    videoDevices,
    audioOutputDevices,
    preferredAudioDeviceId,
    preferredAudioOutputDeviceId,
    preferredVideoDeviceId,
    preferredMediaDeviceError,
  } = useAppSelector(s => s.dashboard);

  useEffect(() => {
    detectrtc.load(() => {
      const hasCamPerm = detectrtc.isWebsiteHasWebcamPermissions;
      const hasMicPerm = detectrtc.isWebsiteHasMicrophonePermissions;
      const hasBothPerm = hasCamPerm && hasMicPerm;
      setHasMediaPermissions(hasBothPerm);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (preferredMediaDeviceError) {
      notification.warn({
        message: 'Media device error',
        description: preferredMediaDeviceError,
        duration: 0,
      });
      dispatch(clearPreferredMediaDeviceError());
    }
  }, [preferredMediaDeviceError, dispatch]);

  useEffect(() => {
    if (!camStreamOut || !isOutgoingAudioEnabled) {
      [
        ...document.querySelectorAll<HTMLElement>(
          '.SalesfloorRoomSettingsItem__meter-level',
        ),
      ].forEach(level => {
        level.style.background = '#101224';
        level.style.borderColor = 'transparent';
      });
      return;
    }
    const audioContext = new AudioContext();
    const audioSource = audioContext.createMediaStreamSource(camStreamOut);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.minDecibels = -127;
    analyser.maxDecibels = 0;
    analyser.smoothingTimeConstant = 0.4;
    audioSource.connect(analyser);
    const volumes = new Uint8Array(analyser.frequencyBinCount);
    const volumeCallback = () => {
      analyser.getByteFrequencyData(volumes);
      let volumeSum = 0;
      for (const volume of volumes) volumeSum += volume;
      const averageVolume = volumeSum / volumes.length;
      const volumePercent = (averageVolume / 127) * 100;
      const levels = [
        ...document.querySelectorAll<HTMLElement>(
          '.SalesfloorRoomSettingsItem__meter-level',
        ),
      ];
      const numberOfLevelsToColor = Math.round((volumePercent * levels.length) / 100);
      const levelsToColor = levels.slice(0, numberOfLevelsToColor);

      for (const level of levels) {
        level.style.background = '#101224';
        level.style.borderColor = 'transparent';
      }
      for (const level of levelsToColor) {
        level.style.background = '#12B76A';
        level.style.borderColor = '#10A35E';
      }
    };
    const interval = setInterval(volumeCallback, 100);
    return () => clearInterval(interval);
  }, [camStreamOut, isOutgoingAudioEnabled]);

  const onChangeVideo = useCallback(
    deviceId => {
      dispatch(setPreferredMediaDevices({ videoDeviceId: deviceId }));
    },
    [dispatch],
  );

  const onChangeAudio = useCallback(
    deviceId => {
      dispatch(setPreferredMediaDevices({ audioDeviceId: deviceId }));
    },
    [dispatch],
  );

  const onChangeAudioOutput = useCallback(
    deviceId => {
      dispatch(setPreferredMediaDevices({ audioOutputDeviceId: deviceId }));
    },
    [dispatch],
  );

  return (
    <>
      <Popover
        visible={open}
        onVisibleChange={o => {
          if (!o) return;
          setOpen(o);
        }}
        trigger="click"
        placement="bottomRight"
        overlayClassName="ChatPopupActiveCallSettings__popover"
        content={
          <div className="ChatPopupActiveCallSettings">
            <SalesfloorRoomSettingsItem
              key={SettingItemType.Cam}
              type={SettingItemType.Cam}
              value={preferredVideoDeviceId}
              onChange={onChangeVideo}
              options={videoDevices}
              disabled={!hasMediaPermissions || isLoading}
              optionOnly
              small
            />
            <SalesfloorRoomSettingsItem
              key={SettingItemType.Mic}
              type={SettingItemType.Mic}
              value={preferredAudioDeviceId}
              onChange={onChangeAudio}
              options={audioDevices}
              disabled={!hasMediaPermissions || isLoading}
              optionOnly
              small
            />
            <SalesfloorRoomSettingsItem
              key={SettingItemType.Speaker}
              type={SettingItemType.Speaker}
              options={audioOutputDevices}
              onChange={onChangeAudioOutput}
              value={preferredAudioOutputDeviceId}
              disabled={!hasMediaPermissions || isLoading}
              small
            />
          </div>
        }
      >
        <button
          className="ChatPopupActiveCallSettings__trigger"
          onClick={() => {
            setOpen(!open);
          }}
        >
          <Icon icon={!open ? 'settingsSalesfloor' : 'check'} width={16} height={16} />
        </button>
      </Popover>
    </>
  );
};
